import { useState, useCallback, useEffect } from 'react';
import { Box, Accordion, Grid, GridItem, Text, VStack } from '@chakra-ui/react';

import ReadOnlyCollapsibleCard from '../components/ReadOnlyCollapsibleCard';
import {
  DwellingInformationCard,
  DateDetailsCard,
  AreaCard,
  RiskFactorsCard,
  CombinedDetailsCard,
} from './additional';
import { ExtendedCoveragesCard } from './location';
import { useAnswerCodes, useLockAwareAccordion } from '../hooks';
import type { Location, AdditionalData } from 'api';
import type { PropertyInsuranceHistoryData } from './policy-info/types';
import { formatAddress, getRiskAddress } from '../constants/locationOptions';
import { createEmptyLocationCoverages } from '../constants/habitationalCoveragesOptions';

interface AdditionalStepProps {
  data: AdditionalData;
  onUpdate: (locations: Location[]) => void;
  isUpdating?: boolean;
  insuranceHistory?: PropertyInsuranceHistoryData;
  onInsuranceHistoryChange?: (
    data: Partial<PropertyInsuranceHistoryData>,
  ) => void;
  province: number;
}

const AdditionalStep = ({
  data,
  onUpdate,
  isUpdating,
  insuranceHistory,
  onInsuranceHistoryChange,
  province,
}: AdditionalStepProps) => {
  const { getLabel, getCodeByLabel } = useAnswerCodes();
  const comprehensiveCode = getCodeByLabel(
    'Comprehensive',
    'location_form_type',
  );
  const [locations, setLocations] = useState<Location[]>(data.locations || []);

  useEffect(() => {
    const incoming = data.locations || [];
    setLocations(
      incoming.map((loc) => {
        const coverages = loc.coverages ?? createEmptyLocationCoverages();
        const formTypeResolved =
          coverages.coverage_form_type || !comprehensiveCode;
        if (loc.coverages && formTypeResolved) {
          return loc;
        }
        return {
          ...loc,
          coverages: {
            ...coverages,
            coverage_form_type:
              coverages.coverage_form_type || comprehensiveCode || '',
          },
        };
      }),
    );
  }, [data.locations, comprehensiveCode]);

  const handleLocationFieldUpdate = useCallback(
    (index: number, field: keyof Location, value: unknown) => {
      setLocations((prevLocations) => {
        const updatedLocations = prevLocations.map((loc, i) =>
          i === index ? { ...loc, [field]: value } : loc,
        );
        onUpdate(updatedLocations);
        return updatedLocations;
      });
    },
    [onUpdate],
  );

  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const accordionProps = useLockAwareAccordion({
    itemCount: locations.length,
    expandedIndex,
    setExpandedIndex,
  });

  const getLocationTitle = (location: Location) => {
    const riskType = location.location_type
      ? getLabel(location.location_type, 'location_type')
      : '';
    const fullAddress = formatAddress(getRiskAddress(location));

    if (riskType && fullAddress) {
      return `${riskType} – ${fullAddress}`;
    }
    return riskType || fullAddress || 'New Location';
  };

  if (locations.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">
          No locations found. Please add locations in the Locations step first.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Accordion {...accordionProps}>
        {locations.map((location, index) => (
          <ReadOnlyCollapsibleCard
            key={location.id}
            title={getLocationTitle(location)}
          >
            <VStack spacing={4} align="stretch">
              <Grid
                templateColumns={{ base: '1fr', lg: 'repeat(10, 1fr)' }}
                gap={4}
                alignItems="stretch"
              >
                <GridItem colSpan={{ base: 1, lg: 3 }} h="100%">
                  <DwellingInformationCard
                    location={location}
                    locationIndex={index}
                    onUpdate={handleLocationFieldUpdate}
                    isUpdating={isUpdating}
                    height="100%"
                  />
                </GridItem>
                <GridItem colSpan={2} h="100%">
                  <DateDetailsCard
                    location={location}
                    locationIndex={index}
                    onUpdate={handleLocationFieldUpdate}
                    isUpdating={isUpdating}
                    height="100%"
                  />
                </GridItem>
                <GridItem colSpan={2} h="100%">
                  <AreaCard
                    location={location}
                    locationIndex={index}
                    onUpdate={handleLocationFieldUpdate}
                    isUpdating={isUpdating}
                    height="100%"
                  />
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }} h="100%">
                  <RiskFactorsCard
                    location={location}
                    locationIndex={index}
                    onUpdate={handleLocationFieldUpdate}
                    isUpdating={isUpdating}
                    height="100%"
                    locations={locations}
                    insuranceHistory={insuranceHistory}
                    onInsuranceHistoryChange={onInsuranceHistoryChange}
                  />
                </GridItem>
              </Grid>

              <Grid
                templateColumns={{ base: '1fr', lg: 'repeat(5, 1fr)' }}
                gap={4}
                alignItems="stretch"
              >
                <GridItem colSpan={{ base: 1, lg: 3 }} h="100%">
                  <CombinedDetailsCard
                    location={location}
                    locationIndex={index}
                    onUpdate={handleLocationFieldUpdate}
                    isUpdating={isUpdating}
                  />
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 2 }} h="100%">
                  <ExtendedCoveragesCard
                    location={location}
                    locationIndex={index}
                    onUpdate={handleLocationFieldUpdate}
                    isUpdating={isUpdating}
                    quoteId={data.id}
                    province={province}
                  />
                </GridItem>
              </Grid>
            </VStack>
          </ReadOnlyCollapsibleCard>
        ))}
      </Accordion>
    </Box>
  );
};

export default AdditionalStep;
